"use client";

import { useState, useCallback, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/track";

/* ---------- filter definitions ---------- */

interface FilterOption {
  label: string;
  value: string;
}

interface FilterGroup {
  key: string;
  titleKey: string;
  options: FilterOption[];
}

const FILTER_GROUPS: FilterGroup[] = [
  {
    key: "texture",
    titleKey: "texture",
    options: [
      { label: "Straight", value: "Straight" },
      { label: "Wavy", value: "Wavy" },
      { label: "Curly", value: "Curly" },
      { label: "Body Wave", value: "Body Wave" },
    ],
  },
  {
    key: "length",
    titleKey: "length",
    options: [
      { label: 'Short (10"–12")', value: '10"-12"' },
      { label: 'Medium (14"–16")', value: '14"-16"' },
      { label: 'Long (18"–20")', value: '18"-20"' },
      { label: 'Extra Long (22"+)', value: '22"-24"' },
    ],
  },
  {
    key: "base",
    titleKey: "baseType",
    options: [
      { label: "Lace Front", value: "Lace Front" },
      { label: "Silk Top", value: "Silk Top" },
      { label: "Mono Top", value: "Mono Top" },
      { label: "Full Lace", value: "Full Lace" },
      { label: "Machine Made", value: "Machine Made" },
    ],
  },
  {
    key: "capSize",
    titleKey: "capSize",
    options: [
      { label: "Petite", value: "Petite" },
      { label: "Average", value: "Average" },
      { label: "Large", value: "Large" },
    ],
  },
  {
    key: "color",
    titleKey: "color",
    options: [
      { label: "Natural Black", value: "Natural Black" },
      { label: "Dark Brown", value: "Dark Brown" },
      { label: "Medium Brown", value: "Medium Brown" },
      { label: "Light Brown", value: "Light Brown" },
      { label: "Blonde", value: "Blonde" },
      { label: "Highlighted", value: "Highlighted" },
      { label: "Ombre", value: "Ombre" },
    ],
  },
  {
    key: "price",
    titleKey: "priceRange",
    options: [
      { label: "Under $2,000", value: "0-2000" },
      { label: "$2,000–$4,000", value: "2000-4000" },
      { label: "$4,000–$6,000", value: "4000-6000" },
      { label: "$6,000+", value: "6000-" },
    ],
  },
];

export { FILTER_GROUPS };
export type { FilterGroup, FilterOption };

/* ---------- accordion section ---------- */

interface FilterSectionProps {
  group: FilterGroup;
  selected: string[];
  onToggle: (key: string, value: string) => void;
  title: string;
}

function FilterSection({ group, selected, onToggle, title }: FilterSectionProps) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-brand-dark/10 py-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-start"
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-dark">
          {title}
          {selected.length > 0 && (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-brand-gold text-[10px] font-bold text-white">
              {selected.length}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-brand-muted transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div className="mt-3 flex flex-col gap-2">
          {group.options.map((opt) => {
            const isChecked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2.5 py-0.5 text-sm text-brand-dark/80 transition-colors hover:text-brand-dark"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggle(group.key, opt.value)}
                  className="h-3.5 w-3.5 rounded-sm border-brand-dark/30 text-brand-gold accent-brand-gold focus:ring-brand-gold"
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- active filter pills ---------- */

export interface ActiveFilter {
  key: string;
  groupKey: string;
  value: string;
  label: string;
}

interface ActiveFilterPillsProps {
  filters: ActiveFilter[];
  onRemove: (groupKey: string, value: string) => void;
  onClearAll: () => void;
  clearAllLabel?: string;
}

export function ActiveFilterPills({
  filters,
  onRemove,
  onClearAll,
  clearAllLabel = "Clear all",
}: ActiveFilterPillsProps) {
  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {filters.map((filter) => (
        <span
          key={filter.key}
          className="flex items-center gap-1 px-3 py-1 bg-brand-gold/10 text-brand-gold text-xs rounded-full"
        >
          {filter.label}
          <button
            onClick={() => onRemove(filter.groupKey, filter.value)}
            className="transition-colors hover:text-brand-gold-dark"
            aria-label={`Remove ${filter.label} filter`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      {filters.length > 0 && (
        <button
          onClick={onClearAll}
          className="px-3 py-1 text-xs text-brand-muted underline underline-offset-2 transition-colors hover:text-brand-dark"
        >
          {clearAllLabel}
        </button>
      )}
    </div>
  );
}

/* ---------- main component ---------- */

export interface ProductFiltersProps {
  className?: string;
  /** If provided, renders active filter badges above the sidebar */
  showActiveBadges?: boolean;
}

export function ProductFilters({
  className,
  showActiveBadges = false,
}: ProductFiltersProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations("product");

  const getSelected = useCallback(
    (key: string): string[] => {
      const val = searchParams.get(key);
      return val ? val.split(",").filter(Boolean) : [];
    },
    [searchParams]
  );

  const totalActiveFilterCount = useMemo(
    () => FILTER_GROUPS.reduce((sum, g) => sum + getSelected(g.key).length, 0),
    [getSelected]
  );

  const activeFilters: ActiveFilter[] = useMemo(
    () =>
      FILTER_GROUPS.flatMap((g) =>
        getSelected(g.key).map((v) => ({
          key: `${g.key}-${v}`,
          groupKey: g.key,
          value: v,
          label: g.options.find((o) => o.value === v)?.label ?? v,
        }))
      ),
    [getSelected]
  );

  const handleToggle = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      const current = params.get(key)?.split(",").filter(Boolean) ?? [];

      const isRemoving = current.includes(value);
      const updated = isRemoving
        ? current.filter((v) => v !== value)
        : [...current, value];

      if (isRemoving) {
        trackEvent("filter_removed", { filterType: key, filterValue: value });
      } else {
        trackEvent("filter_applied", { filterType: key, filterValue: value });
      }

      if (updated.length > 0) {
        params.set(key, updated.join(","));
      } else {
        params.delete(key);
      }

      // Reset to page 1 when filters change
      params.delete("page");

      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleRemove = useCallback(
    (key: string, value: string) => {
      handleToggle(key, value);
    },
    [handleToggle]
  );

  const handleClearAll = useCallback(() => {
    // Preserve sort when clearing filters
    const params = new URLSearchParams();
    const sort = searchParams.get("sort");
    if (sort) params.set("sort", sort);
    router.push(`?${params.toString()}`, { scroll: false });
  }, [router, searchParams]);

  const hasActiveFilters = totalActiveFilterCount > 0;

  return (
    <aside className={cn("w-full", className)}>
      <div className="flex items-center justify-between pb-2">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-dark">
          {t("filterBy")}
          {hasActiveFilters && (
            <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-gold px-1.5 text-[11px] font-bold text-white">
              {totalActiveFilterCount}
            </span>
          )}
        </h2>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="!px-2 !py-1 !text-[11px] !min-h-0 text-brand-gold hover:text-brand-gold-dark"
          >
            {t("clearAll")}
          </Button>
        )}
      </div>

      {showActiveBadges && (
        <ActiveFilterPills
          filters={activeFilters}
          onRemove={handleRemove}
          onClearAll={handleClearAll}
          clearAllLabel={t("clearAll")}
        />
      )}

      {FILTER_GROUPS.map((group) => (
        <FilterSection
          key={group.key}
          group={group}
          selected={getSelected(group.key)}
          onToggle={handleToggle}
          title={t(group.titleKey as "texture" | "length" | "baseType" | "capSize" | "color" | "priceRange")}
        />
      ))}
    </aside>
  );
}

/* ---------- hook: useProductFilters ---------- */

/** Shared hook for filter state — used by mobile filter drawers and no-results pages */
export function useProductFilters() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const getSelected = useCallback(
    (key: string): string[] => {
      const val = searchParams.get(key);
      return val ? val.split(",").filter(Boolean) : [];
    },
    [searchParams]
  );

  const totalActiveFilterCount = useMemo(
    () => FILTER_GROUPS.reduce((sum, g) => sum + getSelected(g.key).length, 0),
    [getSelected]
  );

  const activeFilters: ActiveFilter[] = useMemo(
    () =>
      FILTER_GROUPS.flatMap((g) =>
        getSelected(g.key).map((v) => ({
          key: `${g.key}-${v}`,
          groupKey: g.key,
          value: v,
          label: g.options.find((o) => o.value === v)?.label ?? v,
        }))
      ),
    [getSelected]
  );

  const handleRemoveFilter = useCallback(
    (groupKey: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      const current = params.get(groupKey)?.split(",").filter(Boolean) ?? [];
      const updated = current.filter((v) => v !== value);
      if (updated.length > 0) {
        params.set(groupKey, updated.join(","));
      } else {
        params.delete(groupKey);
      }
      params.delete("page");
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleClearAll = useCallback(() => {
    const params = new URLSearchParams();
    const sort = searchParams.get("sort");
    if (sort) params.set("sort", sort);
    router.push(`?${params.toString()}`, { scroll: false });
  }, [router, searchParams]);

  return {
    totalActiveFilterCount,
    activeFilters,
    handleRemoveFilter,
    handleClearAll,
    hasActiveFilters: totalActiveFilterCount > 0,
  };
}
